import { useAuth } from '../../authentication/hooks/useAuth';

export interface RolePermissions {
  canViewDashboard: boolean;
  canViewCustomers: boolean;
  canEditCustomers: boolean;
  canViewProperties: boolean;
  canEditProperties: boolean;
  canViewJobs: boolean;
  canEditJobs: boolean;
  canViewSchedule: boolean;
  canViewEmployees: boolean;
  canEditEmployees: boolean;
  canViewServices: boolean;
  canEditServices: boolean;
  canViewFinancial: boolean;
  canViewReports: boolean;
  canViewProfile: boolean;
  canEditProfile: boolean;
  canViewUserManagement: boolean
  canEditUserRoles: boolean;
  canManageUsers: boolean;
  canViewPermissions: boolean;
  canViewSettings: boolean;
  canCreateQuotes: boolean;
  canEditSchedule: boolean;
  canViewQuotes: boolean;
  canEditQuotes: boolean;
}

export function useRoleAccess(): RolePermissions {
  const { user } = useAuth();
  
  // Users with isPlatformAdmin metadata get admin permissions
  const role = user?.isPlatformAdmin
    ? 'admin' 
    : user?.role || 'guest';

  const permissions: Record<string, RolePermissions> = {
    admin: {
      canViewDashboard: true,
      canViewCustomers: true,
      canEditCustomers: true,
      canViewProperties: true,
      canEditProperties: true,
      canViewJobs: true,
      canEditJobs: true,
      canViewSchedule: true,
      canViewEmployees: true,
      canEditEmployees: true,
      canViewServices: true,
      canEditServices: true,
      canViewFinancial: true,
      canViewReports: true,
      canViewProfile: true,
      canEditProfile: true,
      canViewUserManagement: true,
      canEditUserRoles: true,
      canManageUsers: true,
      canViewPermissions: true,
      canViewSettings: true,
      canCreateQuotes: true,
      canEditQuotes: true,
      canEditSchedule: true,
      canViewQuotes: true
    },
    manager: {
      canViewDashboard: true,
      canViewCustomers: true,
      canEditCustomers: true,
      canViewProperties: true,
      canEditProperties: true,
      canViewJobs: true,
      canEditJobs: true,
      canViewSchedule: true,
      canViewEmployees: true,
      canEditEmployees: true,
      canViewServices: true,
      canEditServices: false,
      canViewFinancial: false,
      canViewReports: false,
      canViewProfile: true,
      canEditProfile: true,
      canViewUserManagement: false,
      canEditUserRoles: false,
      canManageUsers: false,
      canViewPermissions: false,
      canViewSettings: false,
      canCreateQuotes: true,
      canEditQuotes: true,
      canEditSchedule: true,
      canViewQuotes: true
},
    employee: {
      canViewDashboard: true,
      canViewCustomers: false,
      canEditCustomers: false,
      canViewProperties: false,
      canEditProperties: false,
      canViewJobs: true,
      canEditJobs: false,
      canViewSchedule: true,
      canViewEmployees: false,
      canEditEmployees: false,
      canViewServices: true,
      canEditServices: true,
      canViewFinancial: false,
      canViewReports: false,
      canViewProfile: true,
      canEditProfile: true,
      canViewUserManagement: false,
      canEditUserRoles: false,
      canManageUsers: false,
      canViewPermissions: false,
      canViewSettings: false,
      canCreateQuotes: true,
      canEditQuotes: false,
      canEditSchedule: false,
      canViewQuotes: false
    },
    customer: {
      canViewDashboard: true,
      canViewCustomers: false,
      canEditCustomers: false,
      canViewProperties: true,
      canEditProperties: true,
      canViewJobs: true,
      canEditJobs: false,
      canViewSchedule: false,
      canViewEmployees: false,
      canEditEmployees: false,
      canViewServices: false,
      canEditServices: false,
      canViewFinancial: false,
      canViewReports: false,
      canViewProfile: true,
      canEditProfile: true,
      canViewUserManagement: false,
      canEditUserRoles: false,
      canManageUsers: false,
      canViewPermissions: false,
      canViewSettings: false,
      canCreateQuotes: false,
      canEditQuotes: false,
      canEditSchedule: false,
      canViewQuotes: false
    },
    guest: {
      canViewDashboard: false,
      canViewCustomers: false,
      canEditCustomers: false,
      canViewProperties: false,
      canEditProperties: false,
      canViewJobs: false,
      canEditJobs: false,
      canViewSchedule: false,
      canViewEmployees: false,
      canEditEmployees: false,
      canViewServices: false,
      canEditServices: false,
      canViewFinancial: false,
      canViewReports: false,
      canViewProfile: false,
      canEditProfile: false,
      canViewUserManagement: false,
      canEditUserRoles: false,
      canManageUsers: false,
      canViewPermissions: false,
      canViewSettings: false,
      canCreateQuotes: false,
      canEditQuotes: false,
      canEditSchedule: false,
      canViewQuotes: false
    },
  };

  return permissions[role] || permissions.guest;
}